package core.localStorage

import core.io.dto.MinifiedColourDTO
import core.util.exhaustive
import module.watchlist.ColourListObject

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 14.10.22
 * Author : mariorosshirt
 */

object LocalStorageDAO {
    // ----------------------------------------------------------------------------
    // Constants
    // ----------------------------------------------------------------------------

    var mWatchlist: List<ColourListObject> by LocalStorageListDelegate("watchList")

    var mProductList: List<ColourListObject> by LocalStorageListDelegate("productList")

    var mFixedList: List<MinifiedColourDTO> by LocalStorageListDelegate("fixedList")

    var mSuggestedList: List<MinifiedColourDTO> by LocalStorageListDelegate("suggestedList")

    var mDetailColourObject: ColourListObject? by LocalStorageObjectDelegate("detailColour")

    var mDetailColourProductObject: ColourListObject? by LocalStorageObjectDelegate("detailColourProduct")

    var mSettingsObject: SettingsObject? by LocalStorageObjectDelegate("settingsObject")

    // ----------------------------------------------------------------------------
    // Public properties
    // ----------------------------------------------------------------------------

    data class LocalStorageState(

        val mWatchlist: List<ColourListObject>,

        val mProductList: List<ColourListObject>,

        val mFixedList: List<MinifiedColourDTO>,

        val mSuggestedList: List<MinifiedColourDTO>,

        val mDetailColourObject: ColourListObject?,

        val mDetailColourProductObject: ColourListObject?,

        val mSettingsObject: SettingsObject?
    )

    sealed class ELocalStorageAction {
        class ADD_TO_WATCHLIST(val mColorObject: ColourListObject) : ELocalStorageAction()
        class DELETE_FROM_WATCHLIST(val mColorObject: ColourListObject) : ELocalStorageAction()
        class UPDATE_COLOR_IN_WATCHLIST(val mColorObject: ColourListObject) : ELocalStorageAction()
        class ADD_OR_DELETE_FROM_WATCHLIST(val mColorObject: ColourListObject) : ELocalStorageAction()

        class ADD_TO_PRODUCTLIST(val mColorObject: ColourListObject) : ELocalStorageAction()
        class DELETE_FROM_PRODUCTLIST(val mColorObject: ColourListObject) : ELocalStorageAction()
        class UPDATE_COLOR_IN_PRODUCTLIST(val mColorObject: ColourListObject) : ELocalStorageAction()
        class ADD_OR_DELETE_FROM_PRODUCTLIST(val mColorObject: ColourListObject) : ELocalStorageAction()
        class ADD_COLOUR_LIST_TO_PRODUCTLIST(val mColourObjectList: List<ColourListObject>) : ELocalStorageAction()
        class ADD_TO_FIXEDLIST(val mColorObject: MinifiedColourDTO) : ELocalStorageAction()
        class DELETE_FROM_FIXEDLIST(val mColorObject: MinifiedColourDTO) : ELocalStorageAction()
        class UPDATE_COLOR_IN_FIXEDLIST(val mColorObject: MinifiedColourDTO) : ELocalStorageAction()
        class ADD_OR_DELETE_FROM_FIXEDLIST(val mColorObject: MinifiedColourDTO) : ELocalStorageAction()
        object CLEAR_FIXED_LIST : ELocalStorageAction()

        class ADD_TO_SUGGESTEDLIST(val mColorObject: MinifiedColourDTO) : ELocalStorageAction()
        class DELETE_FROM_SUGGESTEDLIST(val mColorObject: MinifiedColourDTO) : ELocalStorageAction()
        object CLEAR_SUGGESTED_LIST : ELocalStorageAction()

        class ADD_DETAIL_COLOUR(val mColorListObject: ColourListObject) : ELocalStorageAction()
        object DELETE_DETAIL_COLOUR : ELocalStorageAction()

        class ADD_DETAIL_COLOUR_PRODUCT(val mColorObject: ColourListObject) : ELocalStorageAction()

        object DELETE_DETAIL_COLOUR_PRODUCT : ELocalStorageAction()

        object SET_PIWIK_NATIVE_TRACKING : ELocalStorageAction()
    }

    // ----------------------------------------------------------------------------
    // Protected properties
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Private properties
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Init
    // ----------------------------------------------------------------------------
    init {

    }

    // ----------------------------------------------------------------------------
    // Constructor
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // System/Overridden methods
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Public methods
    // ----------------------------------------------------------------------------


    //Watchlist
    fun reducer(state: LocalStorageState, action: ELocalStorageAction): LocalStorageState {
        mWatchlist = state.mWatchlist
        mProductList = state.mProductList
        mFixedList = state.mFixedList
        mSuggestedList = state.mSuggestedList
        mDetailColourObject = state.mDetailColourObject
        mDetailColourProductObject = state.mDetailColourProductObject
        mSettingsObject = state.mSettingsObject
        when (action) {
            is ELocalStorageAction.ADD_TO_WATCHLIST -> addColourToWatchlist(action.mColorObject)
            is ELocalStorageAction.DELETE_FROM_WATCHLIST -> deleteColourFromWatchlist(action.mColorObject)
            is ELocalStorageAction.UPDATE_COLOR_IN_WATCHLIST -> updateColourInWatchlist(action.mColorObject)
            is ELocalStorageAction.ADD_TO_FIXEDLIST -> addColourToFixedlist(action.mColorObject)
            is ELocalStorageAction.ADD_TO_PRODUCTLIST -> addColourToProductlist(action.mColorObject)
            is ELocalStorageAction.ADD_OR_DELETE_FROM_PRODUCTLIST -> addOrDeleteFromProductList(action.mColorObject)
            is ELocalStorageAction.DELETE_FROM_FIXEDLIST -> deleteColourFromFixedlist(action.mColorObject)
            is ELocalStorageAction.DELETE_FROM_PRODUCTLIST -> deleteColourFromProductlist(action.mColorObject)
            is ELocalStorageAction.UPDATE_COLOR_IN_FIXEDLIST -> updateColourInFixedlist(action.mColorObject)
            is ELocalStorageAction.UPDATE_COLOR_IN_PRODUCTLIST -> updateColourInProductlist(action.mColorObject)
            is ELocalStorageAction.CLEAR_FIXED_LIST -> clearFixedList()
            is ELocalStorageAction.ADD_OR_DELETE_FROM_FIXEDLIST -> addOrDeleteFromFixedList(action.mColorObject)
            is ELocalStorageAction.ADD_OR_DELETE_FROM_WATCHLIST -> addOrDeleteFromWatchlist(action.mColorObject)
            is ELocalStorageAction.ADD_DETAIL_COLOUR -> setDetailColourObject(action.mColorListObject)
            is ELocalStorageAction.DELETE_DETAIL_COLOUR -> deleteDetailColourObject()
            is ELocalStorageAction.ADD_TO_SUGGESTEDLIST -> addColourToSuggestedlist(action.mColorObject)
            is ELocalStorageAction.CLEAR_SUGGESTED_LIST -> clearSuggestedList()
            is ELocalStorageAction.DELETE_FROM_SUGGESTEDLIST -> deleteColourFromSuggestedlist(action.mColorObject)
            is ELocalStorageAction.ADD_COLOUR_LIST_TO_PRODUCTLIST -> addColourListToProductList(action.mColourObjectList)
            is ELocalStorageAction.ADD_DETAIL_COLOUR_PRODUCT -> setDetailColourProductObject(action.mColorObject)
            is ELocalStorageAction.DELETE_DETAIL_COLOUR_PRODUCT -> deleteDetailColourProductObject()
            is ELocalStorageAction.SET_PIWIK_NATIVE_TRACKING -> setPiwikNativeTracking()
        }.exhaustive
        return LocalStorageState(
            mWatchlist,
            mProductList,
            mFixedList,
            mSuggestedList,
            mDetailColourObject,
            mDetailColourProductObject,
            mSettingsObject
        )
    }

    private fun addColourToWatchlist(colourListObject: ColourListObject) {
        mWatchlist = mWatchlist.toMutableList().apply { add(0, colourListObject) }
    }

    private fun addOrDeleteFromWatchlist(colourListObject: ColourListObject) {
        val currentWatchListObjectIndex = mWatchlist.firstOrNull { colourListObject.mUnique == it.mUnique }
        if (currentWatchListObjectIndex == null) {
            addColourToWatchlist(colourListObject)
        } else {
            deleteColourFromWatchlist(colourListObject)
        }
    }

    private fun updateColourInWatchlist(colourListObject: ColourListObject) {
        val currentWatchListObjectIndex = mWatchlist.indexOfFirst { colourListObject.mUnique == it.mUnique }
        currentWatchListObjectIndex.let {
            mWatchlist = mWatchlist.toMutableList().apply { set(it, colourListObject) }
        }
    }

    private fun deleteColourFromWatchlist(colourListObject: ColourListObject) {
        val currentWatchListObjectIndex = mWatchlist.indexOfFirst { colourListObject.mUnique == it.mUnique }
        currentWatchListObjectIndex.let {
            mWatchlist = mWatchlist.toMutableList().apply { removeAt(it) }
        }
    }

    //Productlist
    private fun addColourToProductlist(colourListObject: ColourListObject) {
        mProductList = mProductList.toMutableList().apply { add(0, colourListObject) }
    }

    private fun addOrDeleteFromProductList(colourListObject: ColourListObject) {
        val currentProductListObjectIndex = mProductList.firstOrNull { colourListObject.mUnique == it.mUnique }
        if (currentProductListObjectIndex == null) {
            addColourToProductlist(colourListObject)
        } else {
            deleteColourFromProductlist(colourListObject)
        }
    }

    private fun updateColourInProductlist(colourListObject: ColourListObject) {
        val currentProductListObjectIndex = mProductList.indexOfFirst { colourListObject.mUnique == it.mUnique }
        currentProductListObjectIndex.let {
            mProductList = mProductList.toMutableList().apply { set(it, colourListObject) }
        }
    }

    private fun deleteColourFromProductlist(colourListObject: ColourListObject) {
        val currentProductListObjectIndex = mProductList.indexOfFirst { colourListObject.mUnique == it.mUnique }
        currentProductListObjectIndex.let {
            mProductList = mProductList.toMutableList().apply { removeAt(it) }
        }
    }

    private fun addColourListToProductList(colourObjectList: List<ColourListObject>) {
        colourObjectList.reversed().forEach { colourListObject ->
            val currentProductListObjectIndex = mProductList.indexOfFirst { colourListObject.mUnique == it.mUnique }
            if (currentProductListObjectIndex == -1) {
                addColourToProductlist(colourListObject)
            } else {
                val tempProductItem = mProductList[currentProductListObjectIndex]
                mProductList = mProductList.toMutableList().apply { removeAt(currentProductListObjectIndex) }
                addColourToProductlist(tempProductItem)
            }
        }
    }

    //Fixed Colours List
    private fun addColourToFixedlist(minifiedColourObject: MinifiedColourDTO) {
        mFixedList = mFixedList.toMutableList().plus(minifiedColourObject)
    }

    private fun addOrDeleteFromFixedList(minifiedColourObject: MinifiedColourDTO) {
        val currentFixedListObjectIndex = mFixedList.firstOrNull { minifiedColourObject.mUnique == it.mUnique }
        if (currentFixedListObjectIndex == null) {
            addColourToFixedlist(minifiedColourObject)
        } else {
            deleteColourFromFixedlist(minifiedColourObject)
        }
    }

    private fun deleteColourFromFixedlist(minifiedColourObject: MinifiedColourDTO) {
        val currentFixedListObjectIndex = mFixedList.indexOfFirst { minifiedColourObject.mUnique == it.mUnique }
        currentFixedListObjectIndex.let {
            mFixedList = mFixedList.toMutableList().apply { removeAt(it) }
        }
    }

    private fun updateColourInFixedlist(minifiedColourObject: MinifiedColourDTO) {
        val currentFixedListObjectIndex = mFixedList.indexOfFirst { minifiedColourObject.mUnique == it.mUnique }
        currentFixedListObjectIndex.let {
            mFixedList = mFixedList.toMutableList().apply { set(it, minifiedColourObject) }
        }
    }

    private fun clearFixedList() {
        mFixedList = listOf()
    }

    private fun addColourToSuggestedlist(minifiedColourObject: MinifiedColourDTO) {
        mSuggestedList = mSuggestedList.toMutableList().plus(minifiedColourObject)
    }

    private fun deleteColourFromSuggestedlist(minifiedColourObject: MinifiedColourDTO) {
        val currentSuggestedListObjectIndex = mSuggestedList.indexOfFirst { minifiedColourObject.mUnique == it.mUnique }
        currentSuggestedListObjectIndex.let {
            mSuggestedList = mSuggestedList.toMutableList().apply { removeAt(it) }
        }
    }

    private fun clearSuggestedList() {
        mSuggestedList = listOf()
    }

    private fun setDetailColourObject(colourListObject: ColourListObject) {
        mDetailColourObject?.let { deleteDetailColourObject() }
        mDetailColourObject = colourListObject
    }

    private fun deleteDetailColourObject() {
        mDetailColourObject = null
    }

    private fun setDetailColourProductObject(colourListObject: ColourListObject) {
        mDetailColourProductObject?.let { deleteDetailColourProductObject() }
        mDetailColourProductObject = colourListObject
    }

    private fun deleteDetailColourProductObject() {
        mDetailColourProductObject = null
    }

    private fun setPiwikNativeTracking() {
        if(mSettingsObject == null)
            mSettingsObject = SettingsObject(true)
        mSettingsObject?.let {
            mSettingsObject = SettingsObject(!it.mPiwikTrackingNativeActive)
        }
    }

    // ----------------------------------------------------------------------------
    // Protected methods
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Private methods
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Interfaces/Listener
    // ----------------------------------------------------------------------------

}